/**
 * Noise Extraction — pure rules (Phaser / DOM / t() free).
 * Sound is numeric + directional lobe; Scene visualizes sonar.
 */
import {
  lootDef,
  noiseExtractionCatalog,
  type LootTier,
} from "../../data/noise-extraction-catalog";
import { withSimRng, type SimRng } from "../../sim";
import {
  NeEvent,
  type NeAlertCause,
  type NeHoldKind,
  type NePursuerMode,
  type NeRunResult,
  type NeSonarTint,
  type NoiseExtractionEvent,
} from "./noise-extraction-events";
import { castLootRimPulse, castSonarPulse, SONAR_LOOT_RADIUS } from "./view/sonar-fx";

export { lobeAmp } from "./noise-lobe";

export type NoisePhase = "playing" | "holding" | "escaped" | "caught";

export type AlertTier = 0 | 1 | 2 | 3;

export type LootNodeState = {
  id: string;
  tier: LootTier;
  x: number;
  y: number;
  taken: boolean;
};

export type PursuerState = {
  id: string;
  x: number;
  y: number;
  mode: NePursuerMode;
  catchDwellSec: number;
  wanderAngle: number;
  facingX: number;
  facingY: number;
  footstepAcc: number;
  footstepCount: number;
  /** Accrues wall-clock for heartbeat presence pulse (movement-independent). */
  heartbeatAcc: number;
  lastHeardX: number;
  lastHeardY: number;
  /** True after sonar contact memory (reuses hear field names). */
  hasHeard: boolean;
  confirmed: boolean;
  /** Contact strength proxy (1 on sonar kiss). */
  heardIntensity: number;
  /** elapsedSec when contact memory expires. */
  heardUntilSec: number;
  /** elapsedSec until another contact update is allowed. */
  heardCooldownUntilSec: number;
  /** Normalized emit facing at last player-sonar contact (trail estimate). */
  trailDirX: number;
  trailDirY: number;
  /** Active finite trail (investigate → sweep); not sticky alert. */
  trailValid: boolean;
  /** Index into predicted sweep waypoints (0 = forward). */
  investigateStep: number;
  /** Remaining sweep checks before search. */
  investigateSweepLeft: number;
};

/** Live HUD threat from pursuer modes — not sticky `alertedBySonar`. */
export type NoiseThreatHud = "none" | "exposed" | "chasing";

export function threatHudFromPursuers(
  pursuers: readonly { mode: NePursuerMode }[],
): NoiseThreatHud {
  let exposed = false;
  for (const p of pursuers) {
    if (p.mode === "chase" || p.mode === "catching") return "chasing";
    if (p.mode === "investigate" || p.mode === "sweep") exposed = true;
  }
  return exposed ? "exposed" : "none";
}

export type ExitState = {
  x: number;
  y: number;
};

export type HoldState = {
  kind: NeHoldKind;
  lootId: string | null;
  holdSec: number;
  pulseAcc: number;
};

export type NoiseExtractionState = {
  seed: number;
  phase: NoisePhase;
  elapsedSec: number;
  player: {
    x: number;
    y: number;
    radius: number;
    moveSpeed: number;
    carryingValue: number;
    noise: number;
    goalX: number | null;
    goalY: number | null;
    facingX: number;
    facingY: number;
    footstepAcc: number;
    /** Monotonic step count while walking (sonar stride). */
    footstepCount: number;
    /** Accrues wall-clock for heartbeat presence pulse (movement-independent). */
    heartbeatAcc: number;
    blockStallSec: number;
    moving: boolean;
    idlePulseAcc: number;
    scrapeCoolUntilSec: number;
    /**
     * Careless click intent: raw tap was in solid / no LOS and far.
     * Survives projectWalkable near-face snap so wall impact still scrapes.
     * 0 = no penalty intent.
     */
    scrapeIntentRem: number;
    /**
     * Projected goal distance at tap time (≥ scrapeImpactMinGoalDist only).
     * Keeps far-impact scrape alive when remaining d decays below the threshold.
     */
    moveGoalStartRem: number;
  };
  /** Player HUD: pursuer hit ≥1 by player-caused sonar. */
  alertedBySonar: boolean;
  pursuerSonarHits: number;
  alertTier: AlertTier;
  alertCause: NeAlertCause | null;
  maxAlertTier: AlertTier;
  highLootCount: number;
  loot: LootNodeState[];
  pursuers: PursuerState[];
  exit: ExitState;
  hold: HoldState | null;
  events: NoiseExtractionEvent[];
  endMessageKey: string | null;
  rng: SimRng;
};

export type NoiseHudSnapshot = {
  phase: NoisePhase;
  carryingValue: number;
  alertTier: AlertTier;
  /** Sticky result/telemetry — not live HUD color. */
  alerted: boolean;
  alertCause: NeAlertCause | null;
  /** Live HUD from pursuer modes (`search` → none). */
  threatHud: NoiseThreatHud;
  noise: number;
  noiseRadius: number;
  player: { x: number; y: number; facingX: number; facingY: number; moving: boolean };
  exit: ExitState;
  exitDirAngle: number;
  pursuers: readonly PursuerState[];
  /** Shared loot/exit hold progress 0..1. */
  holdProgress: number;
  holdX: number;
  holdY: number;
  holdKind: NeHoldKind | null;
  endMessageKey: string | null;
  endResult: NeRunResult | null;
  lobeSideAmp: number;
  lobeForwardPower: number;
  sonarScale: number;
};

function mulberry32(seed: number): SimRng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(ax - bx, ay - by);
}

function push(state: NoiseExtractionState, ev: NoiseExtractionEvent): void {
  state.events.push(ev);
}

export function inBlock(
  x: number,
  y: number,
  blocks: readonly { x: number; y: number; w: number; h: number }[],
): boolean {
  for (const b of blocks) {
    const hw = b.w * 0.5;
    const hh = b.h * 0.5;
    if (x >= b.x - hw && x <= b.x + hw && y >= b.y - hh && y <= b.y + hh) {
      return true;
    }
  }
  return false;
}

export type BlockRect = { x: number; y: number; w: number; h: number };

/**
 * Solid map rim — walkable clamp alone doesn't create sonar hits.
 * Walls sit just outside the walkable rect so xMin/xMax stay free of inBlock.
 */
export function mapBoundaryBlocks(thickness = 48): BlockRect[] {
  const m = noiseExtractionCatalog.balance.map;
  const t = thickness;
  const gap = 1; // keep walkable edge outside solid AABB
  const w = m.xMax - m.xMin;
  const h = m.yMax - m.yMin;
  const cx = (m.xMin + m.xMax) * 0.5;
  const cy = (m.yMin + m.yMax) * 0.5;
  return [
    { x: m.xMin - gap - t * 0.5, y: cy, w: t, h: h + t * 2 },
    { x: m.xMax + gap + t * 0.5, y: cy, w: t, h: h + t * 2 },
    { x: cx, y: m.yMin - gap - t * 0.5, w: w + t * 2, h: t },
    { x: cx, y: m.yMax + gap + t * 0.5, w: w + t * 2, h: t },
  ];
}

/** Interior encounter blocks + map rim (collision + sonar). */
export function getSolidBlocks(
  interior: readonly BlockRect[] = noiseExtractionCatalog.encounter.blocks,
): BlockRect[] {
  return [...mapBoundaryBlocks(), ...interior];
}

/** Project to nearest walkable point on map (simple ring search). */
export function projectWalkable(
  x: number,
  y: number,
  blocks: readonly { x: number; y: number; w: number; h: number }[],
): { x: number; y: number } {
  const map = noiseExtractionCatalog.balance.map;
  let px = clamp(x, map.xMin, map.xMax);
  let py = clamp(y, map.yMin, map.yMax);
  if (!inBlock(px, py, blocks)) return { x: px, y: py };
  let best = { x: px, y: py };
  let bestD = Infinity;
  for (let r = 8; r <= 160; r += 8) {
    for (let a = 0; a < 16; a++) {
      const ang = (a / 16) * Math.PI * 2;
      const cx = clamp(px + Math.cos(ang) * r, map.xMin, map.xMax);
      const cy = clamp(py + Math.sin(ang) * r, map.yMin, map.yMax);
      if (inBlock(cx, cy, blocks)) continue;
      const d = dist(px, py, cx, cy);
      if (d < bestD) {
        bestD = d;
        best = { x: cx, y: cy };
      }
    }
    if (bestD < Infinity) return best;
  }
  return { x: map.xMin + 40, y: map.yMax - 40 };
}

function carrySpeedMul(carryingValue: number): number {
  const b = noiseExtractionCatalog.balance;
  return clamp(
    1 - carryingValue * b.carrySpeedMulPerValue,
    b.carrySpeedMulMin,
    1,
  );
}

function refreshMoveSpeed(state: NoiseExtractionState): void {
  const b = noiseExtractionCatalog.balance;
  state.player.moveSpeed =
    b.playerBaseSpeed * carrySpeedMul(state.player.carryingValue);
}

export function noiseRadiusFor(noise: number): number {
  const b = noiseExtractionCatalog.balance;
  return Math.max(8, noise * b.noiseRadiusPerUnit);
}

function setAlert(
  state: NoiseExtractionState,
  next: AlertTier,
  cause: NeAlertCause,
): void {
  if (next === state.alertTier) return;
  const from = state.alertTier;
  state.alertTier = next;
  state.alertCause = cause;
  if (next > state.maxAlertTier) state.maxAlertTier = next;
  push(state, {
    kind: NeEvent.AlertChanged,
    from,
    to: next,
    cause,
    elapsedSec: state.elapsedSec,
    messageKey: "noise.msg.alertSonar",
  });
}

/** Player-caused sonar kissed the pursuer → HUD alert (tier ≥1). */
function notePursuerHitByPlayerSonar(state: NoiseExtractionState): void {
  state.pursuerSonarHits += 1;
  state.alertedBySonar = true;
  if (state.alertTier < 1) setAlert(state, 1, "sonar_hit");
}

/** Beam tip on pursuer circle → owning pursuer (cast radius = body + 4). */
function pursuerAtSonarHit(
  state: NoiseExtractionState,
  hx: number,
  hy: number,
): PursuerState | null {
  const hitR = noiseExtractionCatalog.balance.pursuerRadius + 4 + 2;
  let best: PursuerState | null = null;
  let bestD = Infinity;
  for (const p of state.pursuers) {
    const d = dist(p.x, p.y, hx, hy);
    if (d <= hitR && d < bestD) {
      best = p;
      bestD = d;
    }
  }
  return best;
}

function castPlayerSonarForAlert(
  state: NoiseExtractionState,
  opts: {
    ox: number;
    oy: number;
    facingX: number;
    facingY: number;
    sideAmp: number;
    forwardPower: number;
    scale: number;
    maxRange: number;
    rayCount: number;
    rimEmit?: { cx: number; cy: number; radius: number } | null;
    /** When rim-emitting, omit this loot id from opaque targets. */
    ignoreLootId?: string | null;
  },
): void {
  const b = noiseExtractionCatalog.balance;
  const lootTargets = opts.ignoreLootId
    ? state.loot.filter((l) => l.id !== opts.ignoreLootId)
    : state.loot;
  const shared = {
    scale: opts.scale,
    maxRange: opts.maxRange,
    rayCount: opts.rayCount,
    blocks: getSolidBlocks(),
    loot: lootTargets,
    exit: {
      x: state.exit.x,
      y: state.exit.y,
      radius: b.extractRadius,
    },
    pursuers: state.pursuers.map((p) => ({
      x: p.x,
      y: p.y,
      radius: b.pursuerRadius + 4,
    })),
  };
  const beams = opts.rimEmit
    ? castLootRimPulse({
        cx: opts.rimEmit.cx,
        cy: opts.rimEmit.cy,
        radius: opts.rimEmit.radius,
        ...shared,
      })
    : castSonarPulse({
        ox: opts.ox,
        oy: opts.oy,
        facingX: opts.facingX,
        facingY: opts.facingY,
        sideAmp: opts.sideAmp,
        forwardPower: opts.forwardPower,
        ...shared,
      });
  // Pulse origin (cast ox/oy or loot rim center) — not the kiss point on the body.
  const originX = opts.rimEmit ? opts.rimEmit.cx : opts.ox;
  const originY = opts.rimEmit ? opts.rimEmit.cy : opts.oy;
  const tagged = new Set<PursuerState>();
  for (const beam of beams) {
    if (beam.kind !== "pursuer" || !beam.hit) continue;
    const p = pursuerAtSonarHit(state, beam.hx, beam.hy);
    if (p) tagged.add(p);
  }
  if (tagged.size === 0) return;
  notePursuerHitByPlayerSonar(state);
  for (const p of tagged) {
    // Low alert: investigate toward emission origin + emit facing trail.
    registerSonarContact(
      state,
      p,
      originX,
      originY,
      opts.facingX,
      opts.facingY,
    );
  }
}

function emitPlayerSonar(
  state: NoiseExtractionState,
  opts: {
    ox: number;
    oy: number;
    facingX: number;
    facingY: number;
    sideAmp: number;
    forwardPower: number;
    scale: number;
    footSide?: number;
    isotropic: boolean;
    tint: NeSonarTint;
    lifeMul: number;
    maxRange: number;
    rayCount: number;
    rimEmit?: { cx: number; cy: number; radius: number } | null;
    ignoreLootId?: string | null;
  },
): void {
  castPlayerSonarForAlert(state, {
    ox: opts.ox,
    oy: opts.oy,
    facingX: opts.facingX,
    facingY: opts.facingY,
    sideAmp: opts.sideAmp,
    forwardPower: opts.forwardPower,
    scale: opts.scale,
    maxRange: opts.maxRange,
    rayCount: opts.rayCount,
    rimEmit: opts.rimEmit ?? null,
    ignoreLootId: opts.ignoreLootId ?? null,
  });
  push(state, {
    kind: NeEvent.PlayerSonar,
    x: opts.ox,
    y: opts.oy,
    facingX: opts.facingX,
    facingY: opts.facingY,
    sideAmp: opts.sideAmp,
    forwardPower: opts.forwardPower,
    scale: opts.scale,
    footSide: opts.footSide ?? 0,
    isotropic: opts.isotropic,
    tint: opts.tint,
    lifeMul: opts.lifeMul,
    maxRange: opts.maxRange,
    rayCount: opts.rayCount,
    rimEmit: opts.rimEmit ?? null,
    messageKey: "noise.msg.burst",
  });
}

function endRun(state: NoiseExtractionState, result: NeRunResult): void {
  if (state.phase === "escaped" || state.phase === "caught") return;
  state.phase = result;
  state.hold = null;
  const messageKey =
    result === "escaped" ? "noise.result.escaped" : "noise.result.caught";
  state.endMessageKey = messageKey;
  push(state, {
    kind: NeEvent.RunEnded,
    result,
    carryingValue: state.player.carryingValue,
    maxAlertTier: state.maxAlertTier,
    highLootCount: state.highLootCount,
    elapsedSec: state.elapsedSec,
    messageKey,
  });
}

function addNoise(state: NoiseExtractionState, amount: number): void {
  const b = noiseExtractionCatalog.balance;
  state.player.noise = clamp(state.player.noise + amount, 0, b.noiseMax);
}

/** Axis-separated slide; returns moved delta and whether fully blocked. */
export function axisSlideMove(
  x: number,
  y: number,
  dx: number,
  dy: number,
  blocks: readonly { x: number; y: number; w: number; h: number }[],
): { x: number; y: number; moved: number; blocked: boolean } {
  const map = noiseExtractionCatalog.balance.map;
  let px = x;
  let py = y;
  const tx = clamp(x + dx, map.xMin, map.xMax);
  if (!inBlock(tx, py, blocks)) px = tx;
  const ty = clamp(py + dy, map.yMin, map.yMax);
  if (!inBlock(px, ty, blocks)) py = ty;
  const moved = dist(x, y, px, py);
  const wanted = Math.hypot(dx, dy);
  const blocked = wanted > 0.5 && moved < 0.15;
  return { x: px, y: py, moved, blocked };
}

/** Facing probe hits solid within a short step — head-on wall stop, not parallel graze. */
function isFacingWallStop(
  x: number,
  y: number,
  facingX: number,
  facingY: number,
  blocks: readonly BlockRect[],
  probePx = 22,
): boolean {
  const fl = Math.hypot(facingX, facingY) || 1;
  const px = x + (facingX / fl) * probePx;
  const py = y + (facingY / fl) * probePx;
  return inBlock(px, py, blocks);
}

function hasLos(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  blocks: readonly { x: number; y: number; w: number; h: number }[],
): boolean {
  const d = dist(ax, ay, bx, by);
  const steps = Math.max(4, Math.ceil(d / 12));
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const x = ax + (bx - ax) * t;
    const y = ay + (by - ay) * t;
    if (inBlock(x, y, blocks)) return false;
  }
  return true;
}

/**
 * Predicted sweep waypoint from lastHeard + trailDir (never live player GPS).
 * step 0 = forward; 1 = +perp; 2 = −perp. Each point is projectWalkable'd.
 */
export function trailSweepWaypoint(
  lastHeardX: number,
  lastHeardY: number,
  trailDirX: number,
  trailDirY: number,
  step: number,
  blocks: readonly BlockRect[] = getSolidBlocks(),
): { x: number; y: number } {
  const b = noiseExtractionCatalog.balance;
  const fl = Math.hypot(trailDirX, trailDirY) || 1;
  const fx = trailDirX / fl;
  const fy = trailDirY / fl;
  const px = -fy;
  const py = fx;
  const forward = projectWalkable(
    lastHeardX + fx * b.pursuitSweepForwardPx,
    lastHeardY + fy * b.pursuitSweepForwardPx,
    blocks,
  );
  if (step <= 0) return forward;
  const side = step === 1 ? 1 : -1;
  return projectWalkable(
    forward.x + px * b.pursuitSweepSidePx * side,
    forward.y + py * b.pursuitSweepSidePx * side,
    blocks,
  );
}

function clearPursuerTrail(p: PursuerState): void {
  p.trailValid = false;
  p.hasHeard = false;
  p.heardIntensity = 0;
  p.investigateStep = 0;
  p.investigateSweepLeft = 0;
}

/**
 * Set investigate waypoint (`lastHeard*`) to a smeared world point and lock
 * trailDir from the emit facing. Used for: (1) pursuer sonar → player kiss,
 * (2) player sonar → pursuer tag (pulse emission origin + facing).
 * Vision LOS remains the only path to chase/catch — never steer to live
 * player coords without confirmation.
 */
export function registerSonarContact(
  state: NoiseExtractionState,
  pursuer: PursuerState,
  hitX: number,
  hitY: number,
  facingX: number,
  facingY: number,
): void {
  const b = noiseExtractionCatalog.balance;
  const p = pursuer;
  if (state.elapsedSec < p.heardCooldownUntilSec) return;
  const err = b.sonarContactErrorPx;
  const ang = state.rng() * Math.PI * 2;
  const rad = err * (0.35 + state.rng() * 0.65);
  const rawX = hitX + Math.cos(ang) * rad;
  const rawY = hitY + Math.sin(ang) * rad;
  const walk = projectWalkable(rawX, rawY, getSolidBlocks());
  p.lastHeardX = walk.x;
  p.lastHeardY = walk.y;
  const fl = Math.hypot(facingX, facingY);
  if (fl > 1e-6) {
    p.trailDirX = facingX / fl;
    p.trailDirY = facingY / fl;
  } else {
    p.trailDirX = 0;
    p.trailDirY = 1;
  }
  p.trailValid = true;
  p.investigateStep = 0;
  p.investigateSweepLeft = Math.max(1, Math.floor(b.pursuitSweepCount));
  p.hasHeard = true;
  p.heardIntensity = 1;
  p.heardUntilSec = state.elapsedSec + b.heardMemorySec;
  p.heardCooldownUntilSec = state.elapsedSec + b.heardUpdateCooldownSec;
  if (!p.confirmed && p.mode !== "catching" && p.mode !== "chase") {
    p.mode = "investigate";
  }
}

function emitPursuerSonarStride(
  state: NoiseExtractionState,
  pursuer: PursuerState,
): void {
  const b = noiseExtractionCatalog.balance;
  const p = pursuer;
  p.footstepCount += 1;
  // Shared player stride keys — color is presentation-only.
  const every = Math.max(1, Math.floor(b.sonarFootstepEvery));
  if (p.footstepCount % every !== 0) return;
  const sonarStride = Math.floor(p.footstepCount / every);
  const footSide = sonarStride % 2 === 0 ? 1 : -1;
  const fl = Math.hypot(p.facingX, p.facingY) || 1;
  const fx = p.facingX / fl;
  const fy = p.facingY / fl;
  const off = b.sonarFootOffsetPx * footSide;
  const ox = p.x - fy * off;
  const oy = p.y + fx * off;
  const beams = castSonarPulse({
    ox,
    oy,
    facingX: p.facingX,
    facingY: p.facingY,
    sideAmp: b.noiseLobeSideAmp,
    forwardPower: b.noiseLobeForwardPower,
    scale: 1,
    maxRange: b.sonarMaxRange * b.sonarStrideRangeMul,
    rayCount: Math.max(
      12,
      Math.floor(b.sonarRayCount * b.sonarStrideRayMul),
    ),
    blocks: getSolidBlocks(),
    loot: state.loot,
    exit: {
      x: state.exit.x,
      y: state.exit.y,
      radius: b.extractRadius,
    },
    pursuers: null,
    player: {
      x: state.player.x,
      y: state.player.y,
      radius: b.playerRadius + 4,
    },
  });
  for (const beam of beams) {
    if (beam.kind === "player" && beam.hit) {
      registerSonarContact(
        state,
        p,
        beam.hx,
        beam.hy,
        state.player.facingX,
        state.player.facingY,
      );
      break;
    }
  }
  push(state, {
    kind: NeEvent.PursuerSonar,
    pursuerId: p.id,
    mode: p.mode,
    pulse: "stride",
    x: p.x,
    y: p.y,
    facingX: p.facingX,
    facingY: p.facingY,
    sideAmp: b.noiseLobeSideAmp,
    forwardPower: b.noiseLobeForwardPower,
    scale: 1,
    footSide,
    messageKey: "noise.msg.burst",
  });
}

/**
 * Soft isotropic presence pulse — timer-driven for player + pursuers while
 * alive / in play. Distinct from foot lobe (no footSide, ≪ stride range).
 * Alert / investigate only via existing cast tip-hit geometry.
 */
function emitPlayerHeartbeat(state: NoiseExtractionState): void {
  const b = noiseExtractionCatalog.balance;
  emitPlayerSonar(state, {
    ox: state.player.x,
    oy: state.player.y,
    facingX: state.player.facingX,
    facingY: state.player.facingY,
    sideAmp: 1,
    forwardPower: 0.1,
    scale: b.heartbeatSonarScale,
    footSide: 0,
    isotropic: true,
    tint: "heartbeat",
    lifeMul: b.heartbeatSonarLifeMul,
    maxRange: b.heartbeatSonarMaxRange,
    rayCount: Math.max(8, Math.floor(b.heartbeatSonarRayCount)),
  });
}

function emitPursuerHeartbeat(
  state: NoiseExtractionState,
  pursuer: PursuerState,
): void {
  const b = noiseExtractionCatalog.balance;
  const p = pursuer;
  const beams = castSonarPulse({
    ox: p.x,
    oy: p.y,
    facingX: p.facingX,
    facingY: p.facingY,
    sideAmp: 1,
    forwardPower: 0.1,
    scale: b.heartbeatSonarScale,
    maxRange: b.heartbeatSonarMaxRange,
    rayCount: Math.max(8, Math.floor(b.heartbeatSonarRayCount)),
    blocks: getSolidBlocks(),
    loot: state.loot,
    exit: {
      x: state.exit.x,
      y: state.exit.y,
      radius: b.extractRadius,
    },
    pursuers: null,
    player: {
      x: state.player.x,
      y: state.player.y,
      radius: b.playerRadius + 4,
    },
  });
  for (const beam of beams) {
    if (beam.kind === "player" && beam.hit) {
      registerSonarContact(
        state,
        p,
        beam.hx,
        beam.hy,
        state.player.facingX,
        state.player.facingY,
      );
      break;
    }
  }
  push(state, {
    kind: NeEvent.PursuerSonar,
    pursuerId: p.id,
    mode: p.mode,
    pulse: "heartbeat",
    x: p.x,
    y: p.y,
    facingX: p.facingX,
    facingY: p.facingY,
    sideAmp: 1,
    forwardPower: 0.1,
    scale: b.heartbeatSonarScale,
    footSide: 0,
    messageKey: "noise.msg.burst",
  });
}

function tickHeartbeat(state: NoiseExtractionState, dt: number): void {
  if (state.phase !== "playing" && state.phase !== "holding") return;
  const b = noiseExtractionCatalog.balance;
  const interval = Math.max(0.05, b.heartbeatIntervalSec);
  state.player.heartbeatAcc += dt;
  if (state.player.heartbeatAcc >= interval) {
    state.player.heartbeatAcc = 0;
    emitPlayerHeartbeat(state);
  }
  for (const p of state.pursuers) {
    p.heartbeatAcc += dt;
    if (p.heartbeatAcc >= interval) {
      p.heartbeatAcc = 0;
      emitPursuerHeartbeat(state, p);
    }
  }
}

function makePursuerState(
  spawn: { x: number; y: number },
  id: string,
  rng: SimRng,
): PursuerState {
  return {
    id,
    x: spawn.x,
    y: spawn.y,
    mode: "search",
    catchDwellSec: 0,
    wanderAngle: rng() * Math.PI * 2,
    facingX: 0,
    facingY: 1,
    footstepAcc: 0,
    footstepCount: 0,
    heartbeatAcc: rng() * noiseExtractionCatalog.balance.heartbeatIntervalSec,
    lastHeardX: spawn.x,
    lastHeardY: spawn.y,
    hasHeard: false,
    confirmed: false,
    heardIntensity: 0,
    heardUntilSec: 0,
    heardCooldownUntilSec: 0,
    trailDirX: 0,
    trailDirY: 1,
    trailValid: false,
    investigateStep: 0,
    investigateSweepLeft: 0,
  };
}

function cancelHold(state: NoiseExtractionState): void {
  if (!state.hold) return;
  const kind = state.hold.kind;
  state.hold = null;
  if (state.phase === "holding") state.phase = "playing";
  push(state, {
    kind: NeEvent.HoldCancelled,
    holdKind: kind,
    messageKey:
      kind === "exit" ? "noise.msg.extractCancel" : "noise.msg.lootCancel",
  });
}

export function createNoiseExtractionState(
  seed = noiseExtractionCatalog.encounter.seed,
): NoiseExtractionState {
  const rng = mulberry32(seed);
  return withSimRng(rng, () => {
    const enc = noiseExtractionCatalog.encounter;
    const b = noiseExtractionCatalog.balance;
    const spawn = projectWalkable(
      enc.playerSpawn.x,
      enc.playerSpawn.y,
      getSolidBlocks(enc.blocks),
    );
    const state: NoiseExtractionState = {
      seed,
      phase: "playing",
      elapsedSec: 0,
      player: {
        x: spawn.x,
        y: spawn.y,
        radius: b.playerRadius,
        moveSpeed: b.playerBaseSpeed,
        carryingValue: 0,
        noise: 0,
        goalX: null,
        goalY: null,
        facingX: 0,
        facingY: -1,
        footstepAcc: 0,
        footstepCount: 0,
        heartbeatAcc: 0,
        blockStallSec: 0,
        moving: false,
        idlePulseAcc: 0,
        scrapeCoolUntilSec: 0,
        scrapeIntentRem: 0,
        moveGoalStartRem: 0,
      },
      alertedBySonar: false,
      pursuerSonarHits: 0,
      alertTier: 0,
      alertCause: null,
      maxAlertTier: 0,
      highLootCount: 0,
      loot: enc.loot.map((l) => ({
        id: l.id,
        tier: l.tier,
        x: l.x,
        y: l.y,
        taken: false,
      })),
      pursuers: enc.pursuerSpawns.map((spawn, i) =>
        makePursuerState(spawn, `p${i}`, rng),
      ),
      exit: { ...enc.exit },
      hold: null,
      events: [],
      endMessageKey: null,
      rng,
    };
    push(state, {
      kind: NeEvent.RunStarted,
      seed,
      messageKey: "noise.msg.runStarted",
    });
    return state;
  });
}

export function applyMoveTarget(
  state: NoiseExtractionState,
  x: number,
  y: number,
): void {
  if (state.phase !== "playing" && state.phase !== "holding") return;
  const b = noiseExtractionCatalog.balance;
  const enc = noiseExtractionCatalog.encounter;
  const blocks = getSolidBlocks(enc.blocks);
  const map = b.map;
  const rawX = clamp(x, map.xMin, map.xMax);
  const rawY = clamp(y, map.yMin, map.yMax);
  const rawDist = dist(state.player.x, state.player.y, rawX, rawY);
  const careless =
    rawDist >= b.scrapeImpactMinGoalDist &&
    (inBlock(rawX, rawY, blocks) ||
      !hasLos(state.player.x, state.player.y, rawX, rawY, blocks));
  // Movement goal stays walkable; scrape intent remembers the raw far tap.
  state.player.scrapeIntentRem = careless ? rawDist : 0;
  const p = projectWalkable(x, y, blocks);
  state.player.goalX = p.x;
  state.player.goalY = p.y;
  state.player.blockStallSec = 0;
  const dx = p.x - state.player.x;
  const dy = p.y - state.player.y;
  const d = Math.hypot(dx, dy);
  state.player.moveGoalStartRem =
    d >= b.scrapeImpactMinGoalDist ? d : 0;
  if (d > 1) {
    state.player.facingX = dx / d;
    state.player.facingY = dy / d;
  }
  if (state.hold) cancelHold(state);
}

function emitFootstep(state: NoiseExtractionState, scale: number): void {
  const b = noiseExtractionCatalog.balance;
  const carryBoost = 1 + state.player.carryingValue * 0.012;
  state.player.footstepCount += 1;
  const every = Math.max(1, Math.floor(b.sonarFootstepEvery));
  const emitSonar = state.player.footstepCount % every === 0;
  const sonarStride = Math.floor(state.player.footstepCount / every);
  const footSide = sonarStride % 2 === 0 ? 1 : -1;
  addNoise(state, b.footstepPulseNoise * scale * carryBoost);
  push(state, {
    kind: NeEvent.Footstep,
    x: state.player.x,
    y: state.player.y,
    facingX: state.player.facingX,
    facingY: state.player.facingY,
    sideAmp: b.noiseLobeSideAmp,
    forwardPower: b.noiseLobeForwardPower,
    scale: scale * carryBoost,
    footSide,
    emitSonar,
    messageKey: "noise.msg.burst",
  });
  if (emitSonar) {
    const fl = Math.hypot(state.player.facingX, state.player.facingY) || 1;
    const fx = state.player.facingX / fl;
    const fy = state.player.facingY / fl;
    const off = b.sonarFootOffsetPx * footSide;
    const ox = state.player.x - fy * off;
    const oy = state.player.y + fx * off;
    castPlayerSonarForAlert(state, {
      ox,
      oy,
      facingX: state.player.facingX,
      facingY: state.player.facingY,
      sideAmp: b.noiseLobeSideAmp,
      forwardPower: b.noiseLobeForwardPower,
      scale: scale * carryBoost,
      maxRange: b.sonarMaxRange * b.sonarStrideRangeMul,
      rayCount: Math.max(
        12,
        Math.floor(b.sonarRayCount * b.sonarStrideRayMul),
      ),
    });
  }
}

function completeLootHold(state: NoiseExtractionState, lootId: string): void {
  const b = noiseExtractionCatalog.balance;
  const node = state.loot.find((l) => l.id === lootId);
  if (!node || node.taken) return;
  node.taken = true;
  const def = lootDef(node.tier);
  state.player.carryingValue += def.value;
  addNoise(state, def.noise);
  if (node.tier === "high") state.highLootCount += 1;
  refreshMoveSpeed(state);
  const burstRadius = noiseRadiusFor(def.noise) * b.lootBurstRadiusMul;
  push(state, {
    kind: NeEvent.LootCollected,
    tier: node.tier,
    value: def.value,
    noise: def.noise,
    elapsedSec: state.elapsedSec,
    burstRadius,
    messageKey:
      node.tier === "high" ? "noise.msg.lootHigh" : "noise.msg.lootLow",
  });
}

function tryBeginHold(state: NoiseExtractionState): void {
  const b = noiseExtractionCatalog.balance;
  if (state.phase !== "playing") return;
  if (state.player.goalX != null) return;
  if (state.player.moving) return;

  // Prefer loot when overlapping both (rare).
  for (const node of state.loot) {
    if (node.taken) continue;
    if (
      dist(state.player.x, state.player.y, node.x, node.y) >
      b.lootInteractRadius
    ) {
      continue;
    }
    state.phase = "holding";
    state.hold = {
      kind: "loot",
      lootId: node.id,
      holdSec: 0,
      pulseAcc: 0,
    };
    state.player.moving = false;
    push(state, {
      kind: NeEvent.HoldStarted,
      holdKind: "loot",
      lootId: node.id,
      carryingValue: state.player.carryingValue,
      alertTier: state.alertTier,
      elapsedSec: state.elapsedSec,
      messageKey: "noise.msg.looting",
    });
    return;
  }

  if (
    dist(state.player.x, state.player.y, state.exit.x, state.exit.y) >
    b.extractRadius
  ) {
    return;
  }
  state.phase = "holding";
  state.hold = {
    kind: "exit",
    lootId: null,
    holdSec: 0,
    pulseAcc: 0,
  };
  state.player.moving = false;
  push(state, {
    kind: NeEvent.HoldStarted,
    holdKind: "exit",
    lootId: null,
    carryingValue: state.player.carryingValue,
    alertTier: state.alertTier,
    elapsedSec: state.elapsedSec,
    messageKey: "noise.msg.extracting",
  });
}

function emitHoldPulse(state: NoiseExtractionState): void {
  const b = noiseExtractionCatalog.balance;
  const hold = state.hold;
  let rimEmit: { cx: number; cy: number; radius: number } | null = null;
  let ignoreLootId: string | null = null;
  let tint: NeSonarTint = "hold";
  // Loot eating: shell emitters on outer rim → radiate into the world
  if (hold?.kind === "loot" && hold.lootId) {
    const node = state.loot.find((l) => l.id === hold.lootId);
    if (node && !node.taken) {
      rimEmit = { cx: node.x, cy: node.y, radius: SONAR_LOOT_RADIUS };
      ignoreLootId = node.id;
      tint = node.tier === "high" ? "loot_high" : "loot_low";
    }
  }
  emitPlayerSonar(state, {
    ox: rimEmit ? rimEmit.cx : state.player.x,
    oy: rimEmit ? rimEmit.cy : state.player.y,
    facingX: state.player.facingX,
    facingY: state.player.facingY,
    sideAmp: 1,
    forwardPower: 0.1,
    scale: b.holdSonarScale,
    isotropic: true,
    tint,
    lifeMul: b.holdSonarLifeMul,
    maxRange: b.holdSonarMaxRange,
    rayCount: b.holdSonarRayCount,
    rimEmit,
    ignoreLootId,
  });
  addNoise(state, b.footstepPulseNoise * 1.4);
}

function tickHold(state: NoiseExtractionState, dt: number): void {
  const b = noiseExtractionCatalog.balance;
  const hold = state.hold;
  if (!hold) return;

  state.player.goalX = null;
  state.player.goalY = null;
  state.player.moving = false;

  let stillInRange = false;
  if (hold.kind === "loot" && hold.lootId) {
    const node = state.loot.find((l) => l.id === hold.lootId);
    if (node && !node.taken) {
      stillInRange =
        dist(state.player.x, state.player.y, node.x, node.y) <=
        b.lootInteractRadius;
    }
  } else if (hold.kind === "exit") {
    stillInRange =
      dist(state.player.x, state.player.y, state.exit.x, state.exit.y) <=
      b.extractRadius;
  }

  if (!stillInRange) {
    cancelHold(state);
    return;
  }

  hold.holdSec += dt;
  hold.pulseAcc += dt;
  while (hold.pulseAcc >= b.holdPulsePeriodSec) {
    hold.pulseAcc -= b.holdPulsePeriodSec;
    emitHoldPulse(state);
  }

  if (hold.holdSec >= b.interactHoldSec) {
    if (hold.kind === "loot" && hold.lootId) {
      completeLootHold(state, hold.lootId);
      state.hold = null;
      state.phase = "playing";
    } else if (hold.kind === "exit") {
      endRun(state, "escaped");
    }
  }
}

function emitWallScrape(state: NoiseExtractionState, goalRem: number): void {
  const b = noiseExtractionCatalog.balance;
  if (state.elapsedSec < state.player.scrapeCoolUntilSec) return;
  const scale = clamp(
    b.scrapeScaleMin + goalRem * b.scrapeScalePerGoalPx,
    b.scrapeScaleMin,
    b.scrapeScaleMax,
  );
  state.player.scrapeCoolUntilSec = state.elapsedSec + b.scrapeCooldownSec;
  addNoise(state, b.scrapeNoise * scale);
  emitPlayerSonar(state, {
    ox: state.player.x,
    oy: state.player.y,
    facingX: state.player.facingX,
    facingY: state.player.facingY,
    sideAmp: 1,
    forwardPower: 0.1,
    scale,
    isotropic: true,
    tint: "orange",
    lifeMul: b.scrapeSonarLifeMul,
    maxRange:
      b.sonarMaxRange *
      (0.85 + scale * 0.45) *
      b.scrapeSonarMaxRangeMul,
    rayCount: Math.max(16, Math.floor(b.sonarRayCount * 0.95)),
  });
}

function tickPlayerMove(state: NoiseExtractionState, dt: number): void {
  const b = noiseExtractionCatalog.balance;
  const { player } = state;
  const enc = noiseExtractionCatalog.encounter;
  player.moving = false;
  if (player.goalX == null || player.goalY == null) {
    player.blockStallSec = 0;
    return;
  }
  const dx = player.goalX - player.x;
  const dy = player.goalY - player.y;
  const d = Math.hypot(dx, dy);
  const ux = d > 0 ? dx / d : player.facingX;
  const uy = d > 0 ? dy / d : player.facingY;
  if (d < 4) {
    const blocks = getSolidBlocks(enc.blocks);
    const facingStop = isFacingWallStop(player.x, player.y, ux, uy, blocks);
    let arrivalScrapeRem = player.scrapeIntentRem;
    if (
      arrivalScrapeRem < b.scrapeImpactMinGoalDist &&
      player.moveGoalStartRem >= b.scrapeImpactMinGoalDist &&
      facingStop
    ) {
      arrivalScrapeRem = player.moveGoalStartRem;
    }
    // Far intent (≥ scrapeImpactMinGoalDist) keeps full-scale scrape; flush facing
    // wall stop always tings at least scrapeScaleMin even when rem is weak.
    if (arrivalScrapeRem >= b.scrapeImpactMinGoalDist || facingStop) {
      emitWallScrape(state, arrivalScrapeRem);
    }
    player.x = player.goalX;
    player.y = player.goalY;
    player.goalX = null;
    player.goalY = null;
    player.blockStallSec = 0;
    player.scrapeIntentRem = 0;
    player.moveGoalStartRem = 0;
    return;
  }
  player.facingX = ux;
  player.facingY = uy;
  const step = player.moveSpeed * dt;
  const wantAlong = Math.min(step, d);
  const wantX = ux * wantAlong;
  const wantY = uy * wantAlong;
  const prevX = player.x;
  const prevY = player.y;
  const blocks = getSolidBlocks(enc.blocks);
  const slid = axisSlideMove(player.x, player.y, wantX, wantY, blocks);
  const progressTowardGoal = (slid.x - prevX) * ux + (slid.y - prevY) * uy;
  const scrapeDist = Math.max(
    d,
    player.scrapeIntentRem,
    player.moveGoalStartRem,
  );
  const farImpact = scrapeDist >= b.scrapeImpactMinGoalDist;
  // Axis-slide can travel along a wall while facing progress dies — still impact
  // when a far goal / careless intent remains. Parallel graze that closes on an
  // open goal keeps progressTowardGoal high and stays silent.
  const headOnImpact =
    wantAlong > 0.5 && progressTowardGoal < wantAlong * 0.35;
  const facingStop = isFacingWallStop(player.x, player.y, ux, uy, blocks);
  // Flush blocked / head-on into a face always min-tings; farImpact only gates
  // non-contact progress stalls (e.g. parallel scrape with remaining far goal).
  const impact =
    slid.blocked || (headOnImpact && (farImpact || facingStop));

  if (slid.moved > 0.05) {
    player.moving = true;
    player.x = slid.x;
    player.y = slid.y;
    if (!impact) player.blockStallSec = 0;
    const noiseGain =
      slid.moved * b.footstepNoisePerPx * (1 + player.carryingValue * 0.01);
    addNoise(state, noiseGain);
    player.footstepAcc += slid.moved;
    while (player.footstepAcc >= b.footstepIntervalPx) {
      player.footstepAcc -= b.footstepIntervalPx;
      emitFootstep(state, 1);
    }
  }

  if (impact) {
    emitWallScrape(state, scrapeDist);
    player.scrapeIntentRem = 0;
    player.blockStallSec += dt;
    if (player.blockStallSec >= b.blockStallClearSec) {
      player.goalX = null;
      player.goalY = null;
      player.blockStallSec = 0;
      player.scrapeIntentRem = 0;
      player.moveGoalStartRem = 0;
    }
  } else {
    player.blockStallSec = 0;
  }
}

function tickIdleSonar(state: NoiseExtractionState, dt: number): void {
  const b = noiseExtractionCatalog.balance;
  if (state.phase !== "playing") return;
  if (b.idleSonarScale <= 0.01) return;
  if (state.player.moving || state.player.goalX != null) {
    state.player.idlePulseAcc = 0;
    return;
  }
  state.player.idlePulseAcc += dt;
  if (state.player.idlePulseAcc < b.sonarPulsePeriodSec) return;
  state.player.idlePulseAcc = 0;
  emitPlayerSonar(state, {
    ox: state.player.x,
    oy: state.player.y,
    facingX: state.player.facingX,
    facingY: state.player.facingY,
    sideAmp: b.noiseLobeSideAmp,
    forwardPower: b.noiseLobeForwardPower,
    scale: b.idleSonarScale,
    isotropic: false,
    tint: "player",
    lifeMul: 1,
    maxRange: b.sonarMaxRange,
    rayCount: b.sonarRayCount,
  });
}

function tickOnePursuer(
  state: NoiseExtractionState,
  p: PursuerState,
  dt: number,
): void {
  const b = noiseExtractionCatalog.balance;
  const enc = noiseExtractionCatalog.encounter;
  const blocks = getSolidBlocks(enc.blocks);
  const dPlayer = dist(p.x, p.y, state.player.x, state.player.y);
  const inVision =
    dPlayer <= b.pursuerVisionRadius &&
    hasLos(p.x, p.y, state.player.x, state.player.y, blocks);

  // heardUntilSec does not abort investigate/sweep mid-trail (finite sweep ends it).
  if (
    p.hasHeard &&
    !p.trailValid &&
    state.elapsedSec >= p.heardUntilSec
  ) {
    p.hasHeard = false;
    p.heardIntensity = 0;
  }

  // Vision LOS always upgrades to chase/catch — never use live player coords otherwise.
  if (inVision) {
    p.confirmed = true;
    p.mode = dPlayer <= b.pursuerCatchRadius ? "catching" : "chase";
  } else if (p.confirmed) {
    p.confirmed = false;
    if (p.trailValid) p.mode = "investigate";
    else p.mode = "search";
  }

  if (p.mode === "catching" || (p.confirmed && dPlayer <= b.pursuerCatchRadius)) {
    p.mode = "catching";
    p.catchDwellSec += dt;
    if (p.catchDwellSec >= b.pursuerCatchDwellSec) {
      endRun(state, "caught");
      return;
    }
  } else {
    p.catchDwellSec = 0;
  }

  if (
    !p.confirmed &&
    !p.trailValid &&
    p.mode !== "catching" &&
    p.mode !== "chase"
  ) {
    p.mode = "search";
  }

  let speed = b.pursuerSpeedByAlert[state.alertTier] ?? b.pursuerSpeedByAlert[0]!;
  let tx = p.x;
  let ty = p.y;
  if (p.mode === "chase" || p.mode === "catching") {
    tx = state.player.x;
    ty = state.player.y;
  } else if (p.mode === "investigate" && p.trailValid) {
    speed *= Math.max(1, b.pursuerInvestigateSpeedMul);
    tx = p.lastHeardX;
    ty = p.lastHeardY;
    if (dist(p.x, p.y, tx, ty) < 22) {
      // Arrive lastHeard → predicted sweep (not immediate search).
      p.mode = "sweep";
      p.investigateStep = 0;
      const wp = trailSweepWaypoint(
        p.lastHeardX,
        p.lastHeardY,
        p.trailDirX,
        p.trailDirY,
        0,
        blocks,
      );
      tx = wp.x;
      ty = wp.y;
      speed =
        (b.pursuerSpeedByAlert[state.alertTier] ?? b.pursuerSpeedByAlert[0]!) *
        b.pursuitSweepSpeedMul;
    }
  } else if (p.mode === "sweep" && p.trailValid) {
    speed *= b.pursuitSweepSpeedMul;
    const wp = trailSweepWaypoint(
      p.lastHeardX,
      p.lastHeardY,
      p.trailDirX,
      p.trailDirY,
      p.investigateStep,
      blocks,
    );
    tx = wp.x;
    ty = wp.y;
    if (dist(p.x, p.y, tx, ty) < b.pursuitSweepArrivePx) {
      p.investigateStep += 1;
      p.investigateSweepLeft -= 1;
      if (p.investigateSweepLeft <= 0) {
        clearPursuerTrail(p);
        p.mode = "search";
        p.wanderAngle = state.rng() * Math.PI * 2;
        speed = b.pursuerWanderSpeed;
        p.wanderAngle += (state.rng() - 0.5) * 2.2 * dt;
        tx = p.x + Math.cos(p.wanderAngle) * 90;
        ty = p.y + Math.sin(p.wanderAngle) * 90;
      } else {
        const next = trailSweepWaypoint(
          p.lastHeardX,
          p.lastHeardY,
          p.trailDirX,
          p.trailDirY,
          p.investigateStep,
          blocks,
        );
        tx = next.x;
        ty = next.y;
      }
    }
  } else {
    speed = b.pursuerWanderSpeed;
    p.wanderAngle += (state.rng() - 0.5) * 2.2 * dt;
    tx = p.x + Math.cos(p.wanderAngle) * 90;
    ty = p.y + Math.sin(p.wanderAngle) * 90;
  }

  const dx = tx - p.x;
  const dy = ty - p.y;
  const d = Math.hypot(dx, dy) || 1;
  const wantX = (dx / d) * speed * dt;
  const wantY = (dy / d) * speed * dt;
  const slid = axisSlideMove(p.x, p.y, wantX, wantY, blocks);
  if (slid.moved > 0.05) {
    p.facingX = wantX / (Math.hypot(wantX, wantY) || 1);
    p.facingY = wantY / (Math.hypot(wantX, wantY) || 1);
    p.x = slid.x;
    p.y = slid.y;
    p.footstepAcc += slid.moved;
    while (p.footstepAcc >= b.footstepIntervalPx) {
      p.footstepAcc -= b.footstepIntervalPx;
      emitPursuerSonarStride(state, p);
    }
  } else {
    p.x = slid.x;
    p.y = slid.y;
  }
  if (slid.blocked && p.mode === "search") {
    p.wanderAngle += Math.PI * 0.5 + (state.rng() - 0.5);
  }
}

function tickPursuers(state: NoiseExtractionState, dt: number): void {
  for (const p of state.pursuers) {
    if (state.phase === "escaped" || state.phase === "caught") return;
    tickOnePursuer(state, p, dt);
  }
}

export function tickNoiseExtraction(
  state: NoiseExtractionState,
  dt: number,
): void {
  if (state.phase === "escaped" || state.phase === "caught") return;
  const b = noiseExtractionCatalog.balance;
  const step = Math.min(0.05, Math.max(0, dt));
  state.elapsedSec += step;

  if (state.phase === "playing") {
    tickPlayerMove(state, step);
    tryBeginHold(state);
    tickIdleSonar(state, step);
  } else if (state.phase === "holding") {
    tickHold(state, step);
  }

  if (state.phase === "playing" || state.phase === "holding") {
    if (state.player.noise > 0) {
      state.player.noise = Math.max(
        0,
        state.player.noise - b.noiseDecayPerSec * step,
      );
    }
    tickPursuers(state, step);
    tickHeartbeat(state, step);
  }
}

export function drainEvents(
  state: NoiseExtractionState,
): NoiseExtractionEvent[] {
  const out = state.events.slice();
  state.events.length = 0;
  return out;
}

export function getHudSnapshot(state: NoiseExtractionState): NoiseHudSnapshot {
  const b = noiseExtractionCatalog.balance;
  const endResult =
    state.phase === "escaped" || state.phase === "caught" ? state.phase : null;
  const sonarScale = state.player.moving ? 1 : b.idleSonarScale;
  let holdProgress = 0;
  let holdX = state.player.x;
  let holdY = state.player.y;
  let holdKind: NeHoldKind | null = null;
  if (state.hold && state.phase === "holding") {
    holdProgress = clamp(state.hold.holdSec / b.interactHoldSec, 0, 1);
    holdKind = state.hold.kind;
    if (state.hold.kind === "exit") {
      holdX = state.exit.x;
      holdY = state.exit.y;
    } else if (state.hold.lootId) {
      const node = state.loot.find((l) => l.id === state.hold!.lootId);
      if (node) {
        holdX = node.x;
        holdY = node.y;
      }
    }
  }
  return {
    phase: state.phase,
    carryingValue: state.player.carryingValue,
    alertTier: state.alertTier,
    alerted: state.alertedBySonar,
    alertCause: state.alertCause,
    threatHud: threatHudFromPursuers(state.pursuers),
    noise: state.player.noise,
    noiseRadius: noiseRadiusFor(state.player.noise),
    player: {
      x: state.player.x,
      y: state.player.y,
      facingX: state.player.facingX,
      facingY: state.player.facingY,
      moving: state.player.moving,
    },
    exit: state.exit,
    exitDirAngle: Math.atan2(
      state.exit.y - state.player.y,
      state.exit.x - state.player.x,
    ),
    pursuers: state.pursuers.map((p) => ({ ...p })),
    holdProgress,
    holdX,
    holdY,
    holdKind,
    endMessageKey: state.endMessageKey,
    endResult,
    lobeSideAmp: b.noiseLobeSideAmp,
    lobeForwardPower: b.noiseLobeForwardPower,
    sonarScale,
  };
}

export function restartNoiseExtraction(
  seed?: number,
): NoiseExtractionState {
  return createNoiseExtractionState(
    seed ?? noiseExtractionCatalog.encounter.seed,
  );
}
